import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Segmented, Switch } from "@/components/primitives";
import { useTweaksStore } from "@/stores/tweaksStore";
import type { LayoutVariant, TabStyle } from "@/utils/types/ui";

type TweaksPanelProps = {
  onClose: () => void;
};

const ACCENT_HUES = [
  { h: 60, name: "ochre" },
  { h: 25, name: "ember" },
  { h: 200, name: "teal" },
  { h: 340, name: "plum" },
  { h: 145, name: "moss" },
];

export function TweaksPanel({ onClose }: TweaksPanelProps) {
  const tweaks = useTweaksStore();

  return (
    <div className="tweaks-panel">
      <div className="tweaks-panel-head">
        <div className="hstack gap-2">
          <Icon name="settings" size={13} />
          <span className="label">Tweaks</span>
        </div>
        <button
          type="button"
          className="btn xs ghost"
          onClick={onClose}
          aria-label="Close tweaks panel"
        >
          <Icon name="x" size={11} />
        </button>
      </div>

      <div className="tweaks-panel-body">
        <div className="vstack gap-2">
          <div className="label">Accent hue</div>
          <div className="tweaks-hue-row">
            {ACCENT_HUES.map((c) => (
              <button
                key={c.h}
                type="button"
                title={c.name}
                className={cn(
                  "tweaks-hue-swatch",
                  tweaks.accentHue === c.h && "active"
                )}
                onClick={() => tweaks.setTweak("accentHue", c.h)}
                style={{ background: `oklch(0.68 0.14 ${c.h})` }}
              />
            ))}
          </div>
        </div>

        <div className="vstack gap-2">
          <div className="hstack" style={{ justifyContent: "space-between" }}>
            <span className="label">Density</span>
            <span className="tweaks-density-readout">
              {tweaks.density.toFixed(2)}×
            </span>
          </div>
          <input
            type="range"
            min="0.85"
            max="1.15"
            step="0.01"
            value={tweaks.density}
            onChange={(e) =>
              tweaks.setTweak("density", parseFloat(e.target.value))
            }
            className="tweaks-density-input"
          />
        </div>

        <div className="vstack gap-2">
          <div className="label">Workbench layout</div>
          <Segmented<LayoutVariant>
            value={tweaks.layoutVariant}
            onChange={(v) => tweaks.setTweak("layoutVariant", v)}
            options={[
              { value: "balanced", label: "Balanced" },
              { value: "tight", label: "Compact" },
            ]}
          />
        </div>

        <div className="tweaks-row">
          <span className="label">Dark mode</span>
          <Switch
            checked={tweaks.dark}
            onChange={(v) => tweaks.setTweak("dark", v)}
          />
        </div>

        <div className="tweaks-row">
          <span className="label">Collapse sidebar</span>
          <Switch
            checked={tweaks.compactSidebar}
            onChange={(v) => tweaks.setTweak("compactSidebar", v)}
          />
        </div>

        <div className="tweaks-divider" />

        <div className="vstack gap-2">
          <div className="label">Formulation tabs</div>
          <Segmented<TabStyle>
            value={tweaks.tabStyle}
            onChange={(v) => tweaks.setTweak("tabStyle", v)}
            options={[
              { value: "minimal", label: "Minimal" },
              { value: "pill", label: "Pill" },
              { value: "card", label: "Card" },
            ]}
          />
        </div>

        <div className="tweaks-row">
          <span className="label">Tab KPI strip</span>
          <Switch
            checked={tweaks.tabShowKpi}
            onChange={(v) => tweaks.setTweak("tabShowKpi", v)}
          />
        </div>
      </div>
    </div>
  );
}
